import { PipelineService } from "@/gen/omnios/v1/pipeline_connect";
import { PipelineStepResult_Status } from "@/gen/omnios/v1/pipeline_pb";
import type {
	PipelineRun,
	PipelineRunEntity,
} from "@/gen/omnios/v1/pipeline_pb";
import { formatDate } from "@/lib/format";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

// PipelineRunLog not yet in generated code
interface PipelineRunLog {
	id: string;
	level: PipelineRunLog_Level;
	message: string;
	stepId?: string;
	createdAt?: { seconds: bigint; toDate(): Date };
}

// PipelineRunLog_Level is defined in proto but not yet in generated JS
const PipelineRunLog_Level = {
	LEVEL_UNSPECIFIED: 0,
	INFO: 1,
	WARN: 2,
	ERROR: 3,
	DEBUG: 4,
} as const;
type PipelineRunLog_Level =
	(typeof PipelineRunLog_Level)[keyof typeof PipelineRunLog_Level];
import { ProcessingStatusHeader } from "@/components/ProcessingStatus";
import { type Status, StatusIcon } from "@/components/StatusIcon";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import {
	ArrowLeft,
	Box,
	CheckCircle2,
	ChevronRight,
	ClipboardCheck,
	ExternalLink,
	Loader2,
	ScrollText,
	Terminal,
	Timer,
	XCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/background-jobs/$id")({
	component: RunDetailPage,
});

const client = createClient(PipelineService, transport);

function stepToStatus(status: PipelineStepResult_Status): Status {
	switch (status) {
		case PipelineStepResult_Status.COMPLETED:
			return "completed";
		case PipelineStepResult_Status.RUNNING:
			return "running";
		case PipelineStepResult_Status.FAILED:
			return "failed";
		case PipelineStepResult_Status.SKIPPED:
			return "skipped";
		case PipelineStepResult_Status.DISABLED:
			return "disabled";
		default:
			return "pending";
	}
}

function useStepStatusLabel() {
	const { t } = useTranslation("background-jobs");
	return (status: PipelineStepResult_Status) => {
		switch (status) {
			case PipelineStepResult_Status.COMPLETED:
				return t("step_completed");
			case PipelineStepResult_Status.RUNNING:
				return t("step_running");
			case PipelineStepResult_Status.FAILED:
				return t("step_failed");
			case PipelineStepResult_Status.SKIPPED:
				return t("step_skipped");
			case PipelineStepResult_Status.DISABLED:
				return t("step_disabled");
			default:
				return t("step_pending");
		}
	};
}

function formatDuration(ms: number) {
	if (ms < 1000) return `${ms}ms`;
	if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
	const mins = Math.floor(ms / 60_000);
	const secs = Math.floor((ms % 60_000) / 1000);
	return `${mins}m ${secs}s`;
}

function formatTime(d: Date) {
	return formatDate(d, "HH:mm:ss");
}

function formatDateTime(d: Date) {
	return formatDate(d, "dd.MM HH:mm:ss");
}

function logLevelClass(level: PipelineRunLog_Level) {
	switch (level) {
		case PipelineRunLog_Level.ERROR:
			return "text-red-400";
		case PipelineRunLog_Level.WARN:
			return "text-yellow-400";
		case PipelineRunLog_Level.DEBUG:
			return "text-zinc-500";
		default:
			return "text-zinc-300";
	}
}

function logLevelLabel(level: PipelineRunLog_Level) {
	switch (level) {
		case PipelineRunLog_Level.ERROR:
			return "ERR";
		case PipelineRunLog_Level.WARN:
			return "WRN";
		case PipelineRunLog_Level.DEBUG:
			return "DBG";
		default:
			return "INF";
	}
}

type Tab = "steps" | "logs" | "entities";

function RunDetailPage() {
	const { id } = Route.useParams();
	const [tab, setTab] = useState<Tab>("steps");
	const logEndRef = useRef<HTMLDivElement>(null);
	const queryClient = useQueryClient();
	const { t } = useTranslation("background-jobs");

	const cancelMutation = useMutation({
		mutationFn: () => client.cancelPipelineRun({ runId: id }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["pipeline-runs"],
			});
		},
	});

	const { data: allRuns, isLoading } = useQuery({
		queryKey: ["pipeline-run-detail", id],
		queryFn: () =>
			client.listAllRuns({
				page: { pageSize: 200, pageToken: "" },
			}),
		refetchInterval: 3000,
	});

	const run = allRuns?.runs?.find((r) => r.id === id);

	const { data: logsData } = useQuery({
		queryKey: ["pipeline-run-logs", id],
		queryFn: () => client.getRunLogs({ runId: id }),
		refetchInterval: run?.complete ? false : 2000,
	});

	const logs = logsData?.logs ?? [];

	const { data: entitiesData } = useQuery({
		queryKey: ["pipeline-run-entities", id],
		queryFn: () => client.listRunEntities({ runId: id }),
		refetchInterval: run?.complete ? false : 5000,
	});

	const entities = entitiesData?.entities ?? [];

	const runName = run?.pipelineName || run?.pipelineId.slice(0, 8) || "…";

	useBreadcrumbs([
		{
			label: t("title"),
			href: "/background-jobs",
		},
		{ label: runName },
	]);

	useEffect(() => {
		if (tab === "logs") {
			logEndRef.current?.scrollIntoView({
				behavior: "smooth",
			});
		}
	}, [logs.length, tab]);

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-48 w-full" />
			</div>
		);
	}

	if (!run) {
		return (
			<div className="py-16 text-center text-sm text-muted-foreground">
				{t("run_not_found")}
			</div>
		);
	}

	const started = run.startedAt?.toDate();
	const completed = run.completedAt?.toDate();
	const durationMs =
		started && completed
			? completed.getTime() - started.getTime()
			: started
				? Date.now() - started.getTime()
				: null;

	const completedSteps = run.stepResults.filter(
		(s) => s.status === PipelineStepResult_Status.COMPLETED,
	);

	// Extract work_item_id from entrypoint for review navigation
	const entrypoint = run.entrypointData?.toJson() as
		| Record<string, unknown>
		| undefined;
	const reviewWorkItemId =
		(entrypoint?.work_item_id as string) ||
		(entrypoint?.case_id as string) ||
		"";

	return (
		<div className="space-y-6 px-4 py-4">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link
					to="/background-jobs"
					className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
				>
					<ArrowLeft className="h-4 w-4" />
				</Link>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2 flex-wrap">
						<h1 className="text-lg font-semibold truncate">{runName}</h1>
						<ProcessingStatusHeader
							state={
								run.complete && run.progressPct >= 100
									? "completed"
									: run.complete
										? "failed"
										: "running"
							}
						/>
					</div>
				</div>
				<div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
					{started && <span>{formatDateTime(started)}</span>}
					{durationMs != null && (
						<>
							<ChevronRight className="h-3 w-3" />
							<span className="tabular-nums font-medium">
								{formatDuration(durationMs)}
							</span>
						</>
					)}
				</div>
				{run.complete && run.progressPct >= 100 && reviewWorkItemId && (
					<Link to="/work-items/$id" params={{ id: reviewWorkItemId }}>
						<Button size="sm">
							<ClipboardCheck className="h-4 w-4 mr-1.5" />
							{t("review_action")}
						</Button>
					</Link>
				)}
				{!run.complete && (
					<Button
						variant="destructive"
						size="sm"
						onClick={() => cancelMutation.mutate()}
						disabled={cancelMutation.isPending}
					>
						<XCircle className="h-4 w-4 mr-1.5" />
						{cancelMutation.isPending
							? t("cancel_pending")
							: t("cancel_action")}
					</Button>
				)}
			</div>

			{/* Progress bar */}
			<div className="flex items-center gap-3">
				<div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
					<div
						className={cn(
							"h-full rounded-full transition-all duration-500",
							run.complete && run.progressPct >= 100
								? "bg-green-600"
								: run.complete
									? "bg-destructive"
									: "bg-primary",
						)}
						style={{
							width: `${Math.min(run.progressPct, 100)}%`,
						}}
					/>
				</div>
				<span className="text-xs tabular-nums text-muted-foreground w-16 text-right">
					{run.progressPct}%
					{run.stepResults.length > 0 && (
						<>
							{" "}
							· {completedSteps.length}/{run.stepResults.length}
						</>
					)}
				</span>
			</div>

			{run.progressMessage && (
				<p className="text-sm text-muted-foreground">{run.progressMessage}</p>
			)}

			{/* Tabs */}
			<div className="flex items-center gap-1 border-b">
				<TabButton
					active={tab === "steps"}
					onClick={() => setTab("steps")}
					icon={<CheckCircle2 className="h-3.5 w-3.5" />}
					label={t("tab_steps")}
					count={run.stepResults.length}
				/>
				<TabButton
					active={tab === "logs"}
					onClick={() => setTab("logs")}
					icon={<ScrollText className="h-3.5 w-3.5" />}
					label={t("tab_logs")}
					count={logs.length}
				/>
				<TabButton
					active={tab === "entities"}
					onClick={() => setTab("entities")}
					icon={<Box className="h-3.5 w-3.5" />}
					label={t("tab_entities")}
					count={entities.length}
				/>
			</div>

			{/* Tab content */}
			{tab === "steps" && <StepsView run={run} />}
			{tab === "logs" && (
				<LogsView logs={logs} logEndRef={logEndRef} isRunning={!run.complete} />
			)}
			{tab === "entities" && <EntitiesView entities={entities} />}
		</div>
	);
}

function TabButton({
	active,
	onClick,
	icon,
	label,
	count,
}: {
	active: boolean;
	onClick: () => void;
	icon: React.ReactNode;
	label: string;
	count: number;
}) {
	return (
		<Button
			variant="ghost"
			size="sm"
			onClick={onClick}
			className={cn(
				"gap-1.5 px-3 py-2 rounded-none border-b-2 -mb-px",
				active
					? "border-primary text-foreground"
					: "border-transparent text-muted-foreground hover:text-foreground",
			)}
		>
			{icon}
			{label}
			{count > 0 && (
				<span
					className={cn(
						"text-xs tabular-nums rounded-full px-1.5 py-0.5",
						active
							? "bg-primary/10 text-primary"
							: "bg-muted text-muted-foreground",
					)}
				>
					{count}
				</span>
			)}
		</Button>
	);
}

function StepsView({
	run,
}: {
	run: PipelineRun;
}) {
	const { t } = useTranslation("background-jobs");
	const stepStatusLabel = useStepStatusLabel();

	if (run.stepResults.length === 0) {
		return (
			<div className="py-12 text-center text-sm text-muted-foreground">
				{run.complete ? t("no_step_data") : t("waiting_data")}
			</div>
		);
	}

	return (
		<div className="space-y-1.5">
			{run.stepResults.map((sr) => (
				<div
					key={sr.stepId}
					className={cn(
						"flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors",
						sr.status === PipelineStepResult_Status.FAILED
							? "border-destructive/30 bg-destructive/5"
							: sr.status === PipelineStepResult_Status.RUNNING
								? "border-blue-500/30 bg-blue-500/5"
								: sr.status === PipelineStepResult_Status.COMPLETED
									? "border-green-500/20 bg-green-500/5"
									: "border-border",
					)}
				>
					<StatusIcon status={stepToStatus(sr.status)} />
					<span className="text-sm font-medium flex-1 min-w-0 truncate">
						{sr.stepId}
					</span>
					<span className="text-xs text-muted-foreground shrink-0">
						{stepStatusLabel(sr.status)}
					</span>
					{Number(sr.durationMs) > 0 && (
						<span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1 shrink-0">
							<Timer className="h-3 w-3" />
							{formatDuration(Number(sr.durationMs))}
						</span>
					)}

					{sr.errorMessage && <div className="hidden" />}
				</div>
			))}

			{/* Errors section */}
			{run.stepResults.some((sr) => sr.errorMessage) && (
				<div className="mt-4 space-y-2">
					{run.stepResults
						.filter((sr) => sr.errorMessage)
						.map((sr) => (
							<div
								key={`err-${sr.stepId}`}
								className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2"
							>
								<div className="flex items-start gap-2">
									<Terminal className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
									<div className="min-w-0">
										<span className="text-xs font-medium text-destructive">
											{sr.stepId}
										</span>
										<pre className="text-xs text-destructive whitespace-pre-wrap break-all font-mono mt-0.5">
											{sr.errorMessage}
										</pre>
									</div>
								</div>
							</div>
						))}
				</div>
			)}
		</div>
	);
}

function LogsView({
	logs,
	logEndRef,
	isRunning,
}: {
	logs: PipelineRunLog[];
	logEndRef: React.RefObject<HTMLDivElement | null>;
	isRunning: boolean;
}) {
	const { t } = useTranslation("background-jobs");

	if (logs.length === 0) {
		return (
			<div className="py-12 text-center text-sm text-muted-foreground">
				{isRunning ? t("waiting_logs") : t("no_logs")}
			</div>
		);
	}

	return (
		<div className="rounded-lg border bg-zinc-950 overflow-hidden">
			<div className="flex items-center gap-2 px-3 py-1.5 border-b border-zinc-800 bg-zinc-900/60">
				<Terminal className="h-3.5 w-3.5 text-zinc-400" />
				<span className="text-xs font-medium text-zinc-400">
					{t("log_title")}
				</span>
				<span className="text-[10px] text-zinc-600 ml-auto tabular-nums">
					{t("log_entries", { count: logs.length })}
				</span>
			</div>
			<div className="overflow-y-auto max-h-[500px] p-1">
				<div className="font-mono text-[12px] leading-[1.6]">
					{logs.map((log) => {
						const ts = log.createdAt?.toDate();
						return (
							<div
								key={log.id}
								className="flex gap-2 px-2 py-px hover:bg-zinc-900/50 rounded"
							>
								<span className="text-zinc-600 shrink-0 select-none tabular-nums">
									{ts ? formatTime(ts) : ""}
								</span>
								<span
									className={cn(
										"shrink-0 w-7 text-right select-none font-semibold",
										logLevelClass(log.level),
									)}
								>
									{logLevelLabel(log.level)}
								</span>
								{log.stepId && (
									<span className="text-blue-400/70 shrink-0">
										[{log.stepId}]
									</span>
								)}
								<span
									className={cn(
										"break-all",
										log.level === PipelineRunLog_Level.ERROR
											? "text-red-300"
											: log.level === PipelineRunLog_Level.WARN
												? "text-yellow-300"
												: "text-zinc-300",
									)}
								>
									{log.message}
								</span>
							</div>
						);
					})}
					{isRunning && (
						<div className="flex items-center gap-2 px-2 py-1 text-zinc-600">
							<Loader2 className="h-3 w-3 animate-spin" />
							<span className="text-[11px]">{t("waiting")}</span>
						</div>
					)}
					<div ref={logEndRef} />
				</div>
			</div>
		</div>
	);
}

function useEntityTypeLabel() {
	const { t } = useTranslation("activity");
	return {
		product: t("entity_product"),
		order: t("entity_order"),
		work_item: t("entity_work_item"),
		category: t("entity_category"),
	} as Record<string, string>;
}

function entityRoute(entityType: string): string | undefined {
	switch (entityType) {
		case "product":
			return "/catalog/products/$id";
		case "order":
			return "/commerce/orders/$id";
		case "work_item":
			return "/work-items/$id";
		default:
			return undefined;
	}
}

function EntitiesView({
	entities,
}: {
	entities: PipelineRunEntity[];
}) {
	const { t } = useTranslation("background-jobs");
	const entityTypeLabel = useEntityTypeLabel();

	if (entities.length === 0) {
		return (
			<div className="py-12 text-center text-sm text-muted-foreground">
				{t("no_entities")}
			</div>
		);
	}

	return (
		<div className="rounded-md border">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="w-10">#</TableHead>
						<TableHead className="w-24">{t("col_type")}</TableHead>
						<TableHead>{t("col_title")}</TableHead>
						<TableHead className="w-24">{t("col_step")}</TableHead>
						<TableHead className="w-10" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{entities.map((e, idx) => {
						const route = entityRoute(e.entityType);
						const label = entityTypeLabel[e.entityType] ?? e.entityType;
						const title = e.entityTitle || e.entityId.slice(0, 8);

						return (
							<TableRow key={e.id}>
								<TableCell className="text-xs text-muted-foreground tabular-nums">
									{idx + 1}
								</TableCell>
								<TableCell>
									<span className="text-xs font-medium text-muted-foreground">
										{label}
									</span>
								</TableCell>
								<TableCell className="font-medium text-sm truncate max-w-[400px]">
									{route ? (
										<Link
											to={route as any}
											params={
												{
													id: e.entityId,
												} as any
											}
											className="hover:underline"
										>
											{title}
										</Link>
									) : (
										title
									)}
								</TableCell>
								<TableCell className="text-xs text-muted-foreground">
									{e.stepId || "—"}
								</TableCell>
								<TableCell>
									{route && (
										<Link
											to={route as any}
											params={
												{
													id: e.entityId,
												} as any
											}
											className="text-muted-foreground hover:text-foreground"
										>
											<ExternalLink className="h-3.5 w-3.5" />
										</Link>
									)}
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
}
